/**
 * La mappa della commessa a tutto schermo (GI-237).
 *
 * **E' la prima cosa che si vede della commessa**, com'era nel primo
 * prototipo: la linea nello spazio, i sostegni, il volo, dove sono passate le
 * camere e dove stanno le anomalie. Da qui si va nella scheda del sostegno o
 * nel banco sul candidato: e' un posto da cui partire, non un rapporto.
 *
 * **Tre colonne.** A sinistra la ricerca, i filtri e l'elenco dei sostegni;
 * al centro la tela; a destra la scheda di cio' che si e' scelto. La tela
 * prende tutto lo spazio che avanza e non fa scorrere la pagina: si scorre,
 * se serve, dentro le colonne.
 *
 * **Senza sfondo cartografico** (ADR-002): nessuna tessera, nessuna richiesta
 * di rete dalla pagina oltre la lettura del servizio. La tela e' un SVG che si
 * avvicina con la rotella e si trascina col mouse.
 *
 * **Il colore e' il dato, e non viaggia mai da solo.** Il tono del rango
 * viene dalla scala (`--sev-*`), la forma dal glifo, la parola dalla scheda.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { ServiceError } from '../api'
import { FINDING_MANUAL, REVIEW_QUEUE, type Whoami } from '../auth'
import { marginOf, rankOf, type Rank } from '../design/scales'
import * as coverage from '../coverage'
import { decimal } from '../format'
import type { Frame } from '../types'
import { explainService } from '../i18n/explain'
import { useT } from '../i18n/LocaleProvider'
import type { MessageKey } from '../i18n/messages'
import { readLineMap, type LineMap, type MapElement, type MapMark, type MapPhoto, type MapPoint } from '../lineMap'
import { DRAWN, RankGlyph } from './glyphs'
import { pathOf, panned, projectionOf, SIDE, tilesFor, WHOLE, zoomed, type Window } from './projection'
import { clustered } from './clusters'
import { metresPerWorldPx, scaleBar } from './scale'

type Translate = ReturnType<typeof useT>
type Layer = 'flight' | 'spans' | 'poles' | 'photos' | 'marks'
type Ground = 'none' | 'map' | 'satellite'
type Kind = 'ALL' | 'SEGMENT' | 'STATION' | 'BAY' | 'TRACE'
type Chosen = { kind: 'element'; id: string } | { kind: 'photo'; id: string } | { kind: 'mark'; id: string } | null

const LAYERS: { id: Layer; label: MessageKey }[] = [
  { id: 'poles', label: 'map.layerPoles' },
  { id: 'spans', label: 'map.layerSpans' },
  { id: 'flight', label: 'map.layerFlight' },
  { id: 'photos', label: 'map.layerPhotos' },
  { id: 'marks', label: 'map.layerMarks' },
]

const KINDS: Kind[] = ['ALL', 'SEGMENT', 'STATION', 'BAY', 'TRACE']

type Placed = MapElement & { latitude: number; longitude: number }

function placedOf(elements: MapElement[]): Placed[] {
  return elements.filter((one): one is Placed => one.latitude !== null && one.longitude !== null)
}

function rankOfMark(mark: MapMark): Rank {
  return rankOf(mark.final_level ?? mark.level)
}

function violates(mark: MapMark): boolean {
  const delta = marginOf(mark.measured_distance_m, mark.minimum_distance_m)
  return delta !== null && delta < 0
}

function statusKey(status: string): MessageKey {
  if (status === 'covered') return 'coverage.statusCovered'
  if (status === 'declared') return 'coverage.statusDeclared'
  return 'coverage.statusGap'
}

const GROUND_KEY = 'gridfly.map.ground'
const GROUNDS: Ground[] = ['none', 'map', 'satellite']

function groundAtStart(offered: string[]): Ground {
  let remembered: string | null = null
  try {
    remembered = window.localStorage.getItem(GROUND_KEY)
  } catch {
    remembered = null
  }
  if (remembered !== null && (GROUNDS as string[]).includes(remembered)) {
    if (remembered === 'none' || offered.includes(remembered)) return remembered as Ground
  }
  if (offered.includes('satellite')) return 'satellite'
  if (offered.includes('map')) return 'map'
  return 'none'
}

function rememberGround(ground: Ground): void {
  try {
    window.localStorage.setItem(GROUND_KEY, ground)
  } catch {
    // Senza memoria del navigatore si sceglie ogni volta: non e' un guasto.
  }
}

export function MapView({ inspectionId, who }: { inspectionId: string; who: Whoami }): JSX.Element {
  const t = useT()
  const [data, setData] = useState<LineMap | null>(null)
  const [failure, setFailure] = useState<ServiceError | Error | null>(null)

  useEffect(() => {
    let alive = true
    setData(null)
    setFailure(null)
    readLineMap(inspectionId)
      .then((found) => {
        if (alive) setData(found)
      })
      .catch((reason: unknown) => {
        if (alive) setFailure(reason instanceof Error ? reason : new Error(String(reason)))
      })
    return () => {
      alive = false
    }
  }, [inspectionId])

  const error =
    failure === null
      ? null
      : failure instanceof ServiceError
        ? explainService(t, failure.code, failure.message)
        : t('lineMap.unreachable')

  return (
    <section className="map-view" aria-label={t('map.title')}>
      {error !== null && <p role="alert" className="map-notice">{error}</p>}
      {data === null && error === null && (
        <p role="status" className="map-notice">
          {t('lineMap.reading')}
        </p>
      )}
      {data !== null && <Board data={data} who={who} t={t} />}
    </section>
  )
}

function Board({ data, who, t }: { data: LineMap; who: Whoami; t: Translate }): JSX.Element {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [kind, setKind] = useState<Kind>('ALL')
  const [layers, setLayers] = useState<Record<Layer, boolean>>({
    flight: true,
    spans: true,
    poles: true,
    photos: true,
    marks: true,
  })
  const location = useLocation()
  // Chi arriva con «mostra sulla mappa» dal fotogramma o dalla scheda trova
  // l'elemento gia' scelto: l'indirizzo lo nomina, come fa il banco col
  // candidato (GI-248).
  const [chosen, setChosen] = useState<Chosen>(() => {
    const query = new URLSearchParams(location.search)
    const element = query.get('element')
    if (element !== null && data.elements.some((one) => one.element_id === element)) return { kind: 'element', id: element }
    const mark = query.get('mark')
    if (mark !== null && data.marks.some((one) => one.detection_id === mark)) return { kind: 'mark', id: mark }
    return null
  })
  const [window, setWindow] = useState<Window>(WHOLE)
  // Lo sfondo: il satellite, se l'installazione lo offre — una linea MV in
  // campagna si legge sul terreno vero, non sulla carta stradale — altrimenti
  // la carta, altrimenti niente e la linea sta in piedi da sola (ADR-002).
  // La scelta si ricorda: chi preferisce la carta non la risceglie a ogni
  // commessa (GI-247).
  const [ground, setGround] = useState<Ground>(() => groundAtStart(data.basemaps.map((one) => one.id)))
  const chooseGround = (next: Ground): void => {
    rememberGround(next)
    setGround(next)
  }
  const [screenPx, setScreenPx] = useState(800)
  const drag = useRef<{ x: number; y: number; window: Window } | null>(null)
  const canvas = useRef<SVGSVGElement | null>(null)

  useEffect(() => {
    // Quanto e' larga la tela sullo schermo: decide lo zoom delle tessere.
    const node = canvas.current
    if (node === null || typeof ResizeObserver === 'undefined') return
    const measure = (): void => setScreenPx(Math.max(1, node.getBoundingClientRect().width))
    measure()
    const watcher = new ResizeObserver(measure)
    watcher.observe(node)
    return () => watcher.disconnect()
  }, [])

  const placed = useMemo(() => placedOf(data.elements), [data.elements])
  const points = useMemo<MapPoint[]>(
    () => [
      ...placed.map((one): MapPoint => [one.latitude, one.longitude]),
      ...data.spans.flatMap((one) => one.points),
      ...data.trajectory,
      ...data.marks.map((one): MapPoint => [one.latitude, one.longitude]),
      ...data.photos.map((one): MapPoint => [one.latitude, one.longitude]),
    ],
    [placed, data],
  )
  const projection = useMemo(() => projectionOf(points), [points])

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return placed.filter((one) => {
      if (kind !== 'ALL' && one.element_type.toUpperCase() !== kind) return false
      if (needle === '') return true
      return String(one.external_element_id).includes(needle) || one.element_type.toLowerCase().includes(needle)
    })
  }, [placed, query, kind])

  if (points.length === 0) {
    return <p className="map-notice tenue">{t('lineMap.empty')}</p>
  }

  const base = `/inspections/${encodeURIComponent(data.inspection_id)}`
  const basemap = data.basemaps.find((one) => one.id === ground) ?? null
  const tiles = basemap === null ? [] : tilesFor(window, projection, basemap.url, screenPx, basemap.max_zoom)
  const chosenElement = chosen?.kind === 'element' ? placed.find((one) => one.element_id === chosen.id) ?? null : null
  const chosenMark = chosen?.kind === 'mark' ? data.marks.find((one) => one.detection_id === chosen.id) ?? null : null
  const chosenPhoto = chosen?.kind === 'photo' ? data.photos.find((one) => one.photo_id === chosen.id) ?? null : null

  /** Dal pixel dello schermo al punto della tela, tenendo conto della finestra. */
  const at = (event: React.MouseEvent | React.WheelEvent): { x: number; y: number } => {
    const node = canvas.current
    if (node === null) return { x: SIDE / 2, y: SIDE / 2 }
    const box = node.getBoundingClientRect()
    const scale = window.size / Math.max(box.width, 1)
    return { x: window.x + (event.clientX - box.left) * scale, y: window.y + (event.clientY - box.top) * scale }
  }
  // La misura dei segni segue la finestra: avvicinandosi non devono coprire
  // cio' che segnano.
  const k = Math.sqrt(window.size / SIDE)
  // La barra della scala: metri per pixel dello schermo, adesso. La
  // latitudine e' quella media dei punti — su una linea di pochi chilometri
  // la differenza fra gli estremi non si vede.
  const latitude = points.reduce((sum, one) => sum + one[0], 0) / points.length
  const metresPerPx = (metresPerWorldPx(latitude) / projection.scale) * (window.size / screenPx)
  const bar = scaleBar(metresPerPx, screenPx / 4)
  // Le anomalie vicine sullo schermo si raggruppano: la cella segue la
  // finestra, cosi' avvicinandosi i gruppi si sciolgono da soli.
  const drawnMarks = clustered(
    data.marks.map((mark) => ({ mark, x: projection.x([mark.latitude, mark.longitude]), y: projection.y([mark.latitude, mark.longitude]) })),
    5 * k,
    violates,
  )

  return (
    <div className="map-board">
      <aside className="map-side map-left" aria-label={t('map.elements')}>
        <label className="map-search">
          <span className="solo-lettori">{t('map.search')}</span>
          <input
            type="search"
            value={query}
            placeholder={t('map.search')}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <div className="map-kinds" role="group" aria-label={t('map.kind')}>
          {KINDS.map((one) => (
            <button key={one} type="button" aria-pressed={kind === one} onClick={() => setKind(one)}>
              {one === 'ALL' ? t('map.kindAll') : one}
            </button>
          ))}
        </div>
        {data.basemaps.length > 0 && (
          <div className="map-ground" role="group" aria-label={t('map.ground')}>
            <button type="button" aria-pressed={ground === 'none'} onClick={() => chooseGround('none')}>
              {t('map.groundNone')}
            </button>
            {data.basemaps.map((one) => (
              <button key={one.id} type="button" aria-pressed={ground === one.id} onClick={() => chooseGround(one.id as Ground)}>
                {one.id === 'satellite' ? t('map.groundSatellite') : t('map.groundMap')}
              </button>
            ))}
          </div>
        )}
        <fieldset className="map-layers">
          <legend>{t('map.layers')}</legend>
          {LAYERS.map((one) => (
            <label key={one.id}>
              <input
                type="checkbox"
                checked={layers[one.id]}
                onChange={() => setLayers((were) => ({ ...were, [one.id]: !were[one.id] }))}
              />
              {t(one.label)}
            </label>
          ))}
        </fieldset>
        <p className="tenue map-count">
          {t('map.summary', {
            poles: decimal(placed.length),
            photos: decimal(data.photos_total),
            marks: decimal(data.marks.length),
          })}
        </p>
        <ul className="map-list" aria-label={t('map.elements')}>
          {filtered.map((one) => {
            const worst = one.worst_level === null ? null : rankOf(one.worst_level)
            return (
              <li key={one.element_id}>
                <button
                  type="button"
                  className={chosen?.kind === 'element' && chosen.id === one.element_id ? 'map-row on' : 'map-row'}
                  onClick={() => setChosen({ kind: 'element', id: one.element_id })}
                >
                  <span className="t-num">{one.external_element_id}</span>
                  <span className="tenue">{one.element_type}</span>
                  <span className={`map-status map-status-${one.status}`}>{t(statusKey(one.status))}</span>
                  {worst !== null && (
                    <span className="map-worst" style={{ color: `var(--${worst.tone})` }} title={worst.meaning}>
                      {worst.glyph} {worst.name}
                    </span>
                  )}
                </button>
              </li>
            )
          })}
          {filtered.length === 0 && <li className="tenue">{t('map.noneMatch')}</li>}
        </ul>
      </aside>

      <div className="map-canvas-holder">
        <svg
          ref={canvas}
          className={basemap === null ? 'map-canvas' : 'map-canvas map-canvas-ground'}
          role="group"
          aria-label={t('lineMap.with', { poles: placed.length, marks: data.marks.length })}
          viewBox={`${window.x} ${window.y} ${window.size} ${window.size}`}
          preserveAspectRatio="xMidYMid meet"
          onWheel={(event) => {
            event.preventDefault()
            setWindow((was) => zoomed(was, event.deltaY > 0 ? 1.2 : 1 / 1.2, at(event)))
          }}
          onMouseDown={(event) => {
            drag.current = { x: event.clientX, y: event.clientY, window }
          }}
          onMouseMove={(event) => {
            const from = drag.current
            if (from === null || canvas.current === null) return
            const box = canvas.current.getBoundingClientRect()
            const scale = from.window.size / Math.max(box.width, 1)
            setWindow(panned(from.window, (event.clientX - from.x) * scale, (event.clientY - from.y) * scale))
          }}
          onMouseUp={() => {
            drag.current = null
          }}
          onMouseLeave={() => {
            drag.current = null
          }}
        >
          {tiles.map((tile) => (
            <image key={tile.key} href={tile.url} x={tile.x} y={tile.y} width={tile.size} height={tile.size} className="map-tile" />
          ))}
          {layers.flight && data.trajectory.length > 1 && (
            <path className="map-flight" d={pathOf(data.trajectory, projection)} style={{ strokeWidth: 0.5 * k }} />
          )}
          {layers.spans &&
            data.spans.map((span) => (
              <path key={span.ordinal} className="map-span" d={pathOf(span.points, projection)} style={{ strokeWidth: 0.9 * k }} />
            ))}
          {layers.photos &&
            data.photos.map((photo) => {
              const x = projection.x([photo.latitude, photo.longitude])
              const y = projection.y([photo.latitude, photo.longitude])
              const on = chosen?.kind === 'photo' && chosen.id === photo.photo_id
              return (
                <circle
                  key={photo.photo_id}
                  className={on ? 'map-photo on' : 'map-photo'}
                  cx={x}
                  cy={y}
                  r={(on ? 1.2 : 0.7) * k}
                  onClick={() => setChosen({ kind: 'photo', id: photo.photo_id })}
                >
                  <title>{photo.filename}</title>
                </circle>
              )
            })}
          {layers.poles &&
            filtered.map((one) => {
              const x = projection.x([one.latitude, one.longitude])
              const y = projection.y([one.latitude, one.longitude])
              const worst = one.worst_level === null ? null : rankOf(one.worst_level)
              const on = chosen?.kind === 'element' && chosen.id === one.element_id
              return (
                <g
                  key={one.element_id}
                  role="button"
                  tabIndex={0}
                  className={on ? 'map-pole on' : 'map-pole'}
                  data-status={one.status}
                  aria-label={t('lineMap.pole', {
                    id: one.external_element_id,
                    type: one.element_type,
                    status: t(statusKey(one.status)),
                  })}
                  onClick={() => setChosen({ kind: 'element', id: one.element_id })}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      setChosen({ kind: 'element', id: one.element_id })
                    }
                  }}
                >
                  <circle cx={x} cy={y} r={1.7 * k} style={{ strokeWidth: 0.7 * k, ...(worst === null ? {} : { stroke: `var(--${worst.tone})` }) }} />
                  {/* L'etichetta sta in alto a destra, staccata dal segno da un
                      filo: sopra il cerchio copriva il bordo che dice il livello
                      peggiore, cioe' l'unica cosa che il cerchio doveva dire. */}
                  <line className="map-leader" x1={x + 1.9 * k} y1={y - 1.9 * k} x2={x + 3.4 * k} y2={y - 3.4 * k} style={{ strokeWidth: 0.35 * k }} />
                  <text x={x + 3.8 * k} y={y - 3.6 * k} className="map-label" style={{ fontSize: `${2.6 * k}px` }}>
                    {one.external_element_id}
                  </text>
                </g>
              )
            })}
          {layers.marks &&
            drawnMarks.map((drawn) => {
              if (drawn.kind === 'many') {
                const { cluster } = drawn
                const worst = rankOf(cluster.worst)
                const label = t('lineMap.cluster', { n: cluster.marks.length, rank: `${worst.name} ${worst.meaning}` })
                return (
                  <g
                    key={`cluster:${cluster.key}`}
                    role="button"
                    tabIndex={0}
                    className="map-cluster"
                    aria-label={cluster.violates ? `${label}, ${t('lineMap.violation')}` : label}
                    onClick={() => setWindow((was) => zoomed(was, 1 / 2.5, { x: cluster.x, y: cluster.y }))}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        setWindow((was) => zoomed(was, 1 / 2.5, { x: cluster.x, y: cluster.y }))
                      }
                    }}
                  >
                    {cluster.violates && <circle cx={cluster.x} cy={cluster.y} r={4.6 * k} className="map-violation" style={{ strokeWidth: 0.6 * k }} />}
                    <circle cx={cluster.x} cy={cluster.y} r={3 * k} style={{ strokeWidth: 0.8 * k, stroke: `var(--${worst.tone})` }} />
                    <text x={cluster.x} y={cluster.y} className="map-count" style={{ fontSize: `${3 * k}px` }}>
                      {cluster.marks.length}
                    </text>
                  </g>
                )
              }
              const { mark, x, y } = drawn
              const rank = rankOfMark(mark)
              const label = t('lineMap.mark', { code: mark.code, rank: `${rank.name} ${rank.meaning}` })
              return (
                <g
                  key={mark.detection_id}
                  role="button"
                  tabIndex={0}
                  className={chosen?.kind === 'mark' && chosen.id === mark.detection_id ? 'map-mark on' : 'map-mark'}
                  data-confirmed={mark.confirmed ? 'true' : undefined}
                  aria-label={violates(mark) ? `${label}, ${t('lineMap.violation')}` : label}
                  onClick={() => setChosen({ kind: 'mark', id: mark.detection_id })}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      setChosen({ kind: 'mark', id: mark.detection_id })
                    }
                  }}
                >
                  {violates(mark) && <circle cx={x} cy={y} r={4.2 * k} className="map-violation" style={{ strokeWidth: 0.6 * k }} />}
                  <RankGlyph rank={rank} x={x} y={y} scale={k} />
                </g>
              )
            })}
        </svg>
        <div className="map-tools" role="group" aria-label={t('map.zoom')}>
          <button type="button" onClick={() => setWindow((was) => zoomed(was, 1 / 1.4, { x: was.x + was.size / 2, y: was.y + was.size / 2 }))} aria-label={t('map.zoomIn')}>
            +
          </button>
          <button type="button" onClick={() => setWindow((was) => zoomed(was, 1.4, { x: was.x + was.size / 2, y: was.y + was.size / 2 }))} aria-label={t('map.zoomOut')}>
            −
          </button>
          <button type="button" onClick={() => setWindow(WHOLE)}>
            {t('map.fit')}
          </button>
        </div>
        {bar !== null && (
          <div className="map-scale" role="img" aria-label={t('map.scale', { length: bar.label })}>
            <span className="map-scale-bar" style={{ width: `${Math.round(bar.px)}px` }} />
            <span className="t-num">{bar.label}</span>
          </div>
        )}
        <Legend t={t} />
        {basemap !== null && <p className="map-attribution">{basemap.attribution}</p>}
      </div>

      <aside className="map-side map-right" aria-label={t('map.detail')}>
        {chosenElement !== null && (
          <ElementDetail
            one={chosenElement}
            inspectionId={data.inspection_id}
            marks={data.marks.filter((mark) => mark.element_id === chosenElement.element_id)}
            base={base}
            who={who}
            t={t}
            onMark={(id) => setChosen({ kind: 'mark', id })}
            onClose={() => setChosen(null)}
          />
        )}
        {chosenMark !== null && <MarkDetail mark={chosenMark} base={base} t={t} onClose={() => setChosen(null)} navigate={navigate} />}
        {chosenPhoto !== null && <PhotoDetail photo={chosenPhoto} base={base} who={who} t={t} onClose={() => setChosen(null)} />}
        {chosen === null && <p className="tenue map-hint">{t('map.hint')}</p>}
      </aside>
    </div>
  )
}

/** Quanti fotogrammi si mostrano in miniatura: abbastanza da riconoscere il
 *  sostegno, non tutti — per tutti c'e' il fotogrammario filtrato. */
const THUMBS = 6

function ElementDetail({
  one,
  inspectionId,
  marks,
  base,
  who,
  t,
  onMark,
  onClose,
}: {
  one: Placed
  inspectionId: string
  marks: MapMark[]
  base: string
  who: Whoami
  t: Translate
  onMark: (detectionId: string) => void
  onClose: () => void
}): JSX.Element {
  const worst = one.worst_level === null ? null : rankOf(one.worst_level)
  // I fotogrammi che lo inquadrano, in miniatura: dal marcatore al fotogramma
  // in due gesti, senza passare da un elenco (GI-248). La stessa rotta del
  // fotogrammario, filtrata per elemento.
  const [thumbs, setThumbs] = useState<Frame[]>([])
  useEffect(() => {
    let alive = true
    setThumbs([])
    coverage
      .frames(inspectionId, { ...coverage.EMPTY, element: one.element_id, limit: THUMBS, offset: 0 })
      .then((found) => alive && setThumbs(found.frames))
      .catch(() => undefined)
    return () => {
      alive = false
    }
  }, [inspectionId, one.element_id])
  const framesHref = `${base}/search?element=${encodeURIComponent(one.element_id)}`
  return (
    <div className="map-detail">
      <div className="map-detail-head">
        <h3>
          {one.element_type} <span className="t-num">{one.external_element_id}</span>
        </h3>
        <button type="button" onClick={onClose} aria-label={t('map.close')}>
          ×
        </button>
      </div>
      <dl>
        <dt>{t('map.coverage')}</dt>
        <dd className={`map-status map-status-${one.status}`}>{t(statusKey(one.status))}</dd>
        <dt>{t('map.frames')}</dt>
        <dd>{t('map.framesOf', { n: decimal(one.frames), reviewed: decimal(one.reviewed_frames), candidates: decimal(one.candidate_frames) })}</dd>
        <dt>{t('map.findings')}</dt>
        <dd>
          {decimal(one.findings)}
          {worst !== null && (
            <span className="map-worst" style={{ color: `var(--${worst.tone})` }}>
              {' '}
              · {worst.glyph} {worst.name} {worst.meaning}
            </span>
          )}
        </dd>
        <dt>{t('map.coordinates')}</dt>
        <dd className="t-num">
          {one.latitude.toFixed(6)}, {one.longitude.toFixed(6)}
        </dd>
      </dl>
      {thumbs.length > 0 && (
        <ul className="map-thumbs" aria-label={t('map.thumbs')}>
          {thumbs.map((frame) => (
            <li key={frame.photo_id}>
              <Link to={`${framesHref}&q=${encodeURIComponent(frame.filename)}`} title={frame.filename}>
                {frame.thumbnail_url !== null ? (
                  <img src={frame.thumbnail_url} alt={frame.filename} loading="lazy" />
                ) : (
                  <span className="map-thumb-empty t-num">{frame.filename}</span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
      {marks.length > 0 && (
        <ul className="map-marks-list" aria-label={t('map.marksOf')}>
          {marks.map((mark) => {
            const rank = rankOfMark(mark)
            return (
              <li key={mark.detection_id}>
                <button type="button" className="map-link" onClick={() => onMark(mark.detection_id)}>
                  <span style={{ color: `var(--${rank.tone})` }}>
                    {rank.glyph} {rank.name}
                  </span>{' '}
                  {mark.code}
                  {violates(mark) && <span className="tenue"> · {t('lineMap.violation')}</span>}
                </button>
              </li>
            )
          })}
        </ul>
      )}
      <div className="map-actions">
        <Link to={`${base}/elements/${encodeURIComponent(one.element_id)}`}>{t('map.openElement')}</Link>
        {who.may.includes(FINDING_MANUAL) && <Link to={framesHref}>{t('map.framesOfElement')}</Link>}
        {who.may.includes(REVIEW_QUEUE) && one.candidate_frames > 0 && (
          <Link to={`${base}/queue`}>{t('map.openQueue')}</Link>
        )}
        {who.may.includes(FINDING_MANUAL) && <Link to={`${base}/coverage`}>{t('map.openCoverage')}</Link>}
      </div>
    </div>
  )
}

function MarkDetail({
  mark,
  base,
  t,
  onClose,
  navigate,
}: {
  mark: MapMark
  base: string
  t: Translate
  onClose: () => void
  navigate: ReturnType<typeof useNavigate>
}): JSX.Element {
  const rank = rankOfMark(mark)
  const delta = marginOf(mark.measured_distance_m, mark.minimum_distance_m)
  return (
    <div className="map-detail">
      <div className="map-detail-head">
        <h3>
          {mark.code}{' '}
          <span className="map-worst" style={{ color: `var(--${rank.tone})` }}>
            {rank.glyph} {rank.name}
          </span>
        </h3>
        <button type="button" onClick={onClose} aria-label={t('map.close')}>
          ×
        </button>
      </div>
      <dl>
        <dt>{t('map.rank')}</dt>
        <dd>{rank.meaning}</dd>
        <dt>{t('map.source')}</dt>
        <dd>{mark.source}</dd>
        <dt>{t('map.verdict')}</dt>
        <dd>{mark.confirmed ? t('map.confirmed') : mark.decision ?? t('map.waiting')}</dd>
        {delta !== null && (
          <>
            <dt>{t('map.margin')}</dt>
            <dd className="t-num" style={delta < 0 ? { color: 'var(--sev-4)' } : undefined}>
              {delta.toFixed(2)} m
            </dd>
          </>
        )}
      </dl>
      <div className="map-actions">
        <button type="button" onClick={() => navigate(`${base}/candidates/${encodeURIComponent(mark.detection_id)}`)}>
          {t('map.openCandidate')}
        </button>
      </div>
    </div>
  )
}

function PhotoDetail({
  photo,
  base,
  who,
  t,
  onClose,
}: {
  photo: MapPhoto
  base: string
  who: Whoami
  t: Translate
  onClose: () => void
}): JSX.Element {
  return (
    <div className="map-detail">
      <div className="map-detail-head">
        <h3>{t('map.photo')}</h3>
        <button type="button" onClick={onClose} aria-label={t('map.close')}>
          ×
        </button>
      </div>
      <dl>
        <dt>{t('map.file')}</dt>
        <dd className="t-num map-filename">{photo.filename}</dd>
        <dt>{t('map.camera')}</dt>
        <dd>{photo.camera ?? '—'}</dd>
        <dt>{t('map.coordinates')}</dt>
        <dd className="t-num">
          {photo.latitude.toFixed(6)}, {photo.longitude.toFixed(6)}
        </dd>
      </dl>
      <div className="map-actions">
        {who.may.includes(FINDING_MANUAL) && (
          <Link to={`${base}/search?q=${encodeURIComponent(photo.filename)}`}>{t('map.openFrame')}</Link>
        )}
        {who.may.includes(FINDING_MANUAL) && <Link to={`${base}/search`}>{t('map.openPhotos')}</Link>}
      </div>
    </div>
  )
}

function Legend({ t }: { t: Translate }): JSX.Element {
  return (
    <dl className="map-legend">
      {DRAWN.map((rank) => (
        <div key={rank.name}>
          <dt>
            <svg viewBox="0 0 10 10" className="map-swatch" aria-hidden="true">
              <RankGlyph rank={rank} x={5} y={5} />
            </svg>
          </dt>
          <dd>
            {rank.name} · {rank.meaning}
          </dd>
        </div>
      ))}
      <div>
        <dt>
          <svg viewBox="0 0 10 10" className="map-swatch" aria-hidden="true">
            <circle cx={5} cy={5} r={4.2} className="map-violation" />
          </svg>
        </dt>
        <dd>{t('lineMap.legendViolation')}</dd>
      </div>
      <div>
        <dt>
          <svg viewBox="0 0 10 10" className="map-swatch" aria-hidden="true">
            <g className="map-cluster">
              <circle cx={5} cy={5} r={4} style={{ stroke: 'var(--sev-4)' }} />
              <text x={5} y={5} className="map-count" style={{ fontSize: '5px' }}>
                3
              </text>
            </g>
          </svg>
        </dt>
        <dd>{t('lineMap.legendCluster')}</dd>
      </div>
      <div>
        <dt>
          <svg viewBox="0 0 10 10" className="map-swatch" aria-hidden="true">
            <g className="map-pole" data-status="covered">
              <circle cx={5} cy={5} r={2.4} />
            </g>
          </svg>
        </dt>
        <dd>{t('lineMap.legendPole')}</dd>
      </div>
      <div>
        <dt>
          <svg viewBox="0 0 10 10" className="map-swatch" aria-hidden="true">
            <g className="map-pole" data-status="gap">
              <circle cx={5} cy={5} r={2.4} />
            </g>
          </svg>
        </dt>
        <dd>{t('lineMap.legendGap')}</dd>
      </div>
      <div>
        <dt>
          <svg viewBox="0 0 10 10" className="map-swatch" aria-hidden="true">
            <circle cx={5} cy={5} r={1.4} className="map-photo" />
          </svg>
        </dt>
        <dd>{t('map.legendPhoto')}</dd>
      </div>
      <div>
        <dt>
          <svg viewBox="0 0 10 10" className="map-swatch" aria-hidden="true">
            <path className="map-flight" d="M0,7 L10,3" />
          </svg>
        </dt>
        <dd>{t('lineMap.legendFlight')}</dd>
      </div>
    </dl>
  )
}
